import React, { useState, useEffect } from "react";
import { sanityClient } from "../lib/sanityClient";
import MechanicCard from "./MechanicCard";

const MechanicsList = () => {
  const [mechanics, setMechanics] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState("experience"); // Default sorting by experience

  const mechanicsPerPage = 6;

  useEffect(() => {
    const fetchMechanics = async () => {
      try {
        const query = `*[_type == "mechanic"] | order(yearsOfExperience desc) {
          _id,
          name,
          phoneNumber,
          location,
          specialties,
          yearsOfExperience,
          rating
        }`;
        const data = await sanityClient.fetch(query);
        setMechanics(data);
      } catch (error) {
        console.error("Error fetching mechanics:", error);
      }
    };

    fetchMechanics();
  }, []);

  // Filtering mechanics based on search query
  const filteredMechanics = mechanics.filter((mechanic) =>
    mechanic.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    mechanic.specialties.some((specialty) =>
      specialty.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // Sorting function
  const sortedMechanics = [...filteredMechanics].sort((a, b) => {
    switch (sortOption) {
      case "experience":
        return b.yearsOfExperience - a.yearsOfExperience;
      case "rating":
        return b.rating - a.rating;
      case "specialty":
        return a.specialties[0]?.localeCompare(b.specialties[0]);
      default:
        return 0;
    }
  });

  // Pagination logic
  const indexOfLastMechanic = currentPage * mechanicsPerPage;
  const indexOfFirstMechanic = indexOfLastMechanic - mechanicsPerPage;
  const currentMechanics = sortedMechanics.slice(indexOfFirstMechanic, indexOfLastMechanic);

  // Handle page change
  const nextPage = () => {
    if (currentPage < Math.ceil(filteredMechanics.length / mechanicsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="container mx-auto p-4">
      {/* Search Bar */}
      <div className="mb-4 flex justify-center">
        <input
          type="text"
          placeholder="Search mechanics by name or specialty..."
          className="p-2 w-full max-w-lg border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1); // Reset to first page when searching
          }}
        />
      </div>

      {/* Sorting Controls */}
      <div className="flex justify-between mb-4">
        <label className="text-lg font-semibold">
          Sort by:{" "}
          <select
            className="p-2 border rounded-md"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <option value="experience">Experience</option>
            <option value="rating">Rating</option>
            <option value="specialty">Specialty</option>
          </select>
        </label>
      </div>

      {/* Mechanics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentMechanics.length > 0 ? (
          currentMechanics.map((mechanic) => (
            <MechanicCard key={mechanic._id} mechanic={mechanic} />
          ))
        ) : (
          <p className="text-gray-500 text-center col-span-3">No mechanics found.</p>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredMechanics.length > mechanicsPerPage && (
        <div className="flex justify-between mt-6">
          <button
            onClick={prevPage}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-300 text-black rounded-md disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-lg font-semibold">Page {currentPage}</span>
          <button
            onClick={nextPage}
            disabled={currentPage >= Math.ceil(filteredMechanics.length / mechanicsPerPage)}
            className="px-4 py-2 bg-gray-300 text-black rounded-md disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default MechanicsList;
